import jsPDF from 'jspdf';
import 'jspdf-autotable';

// CSF function order for consistent display
const FUNCTION_ORDER = [
  'GOVERN (GV)',
  'IDENTIFY (ID)',
  'PROTECT (PR)',
  'DETECT (DE)',
  'RESPOND (RS)',
  'RECOVER (RC)',
];

// Short display names for PDF space efficiency
const FUNCTION_SHORT = {
  'GOVERN (GV)': 'Govern (GV)',
  'IDENTIFY (ID)': 'Identify (ID)',
  'PROTECT (PR)': 'Protect (PR)',
  'DETECT (DE)': 'Detect (DE)',
  'RESPOND (RS)': 'Respond (RS)',
  'RECOVER (RC)': 'Recover (RC)',
};

/**
 * Map function names to standard format (matches Dashboard.js normalizeFunctionName)
 */
const normalizeFunctionName = (func) => {
  if (!func) return 'Unknown';
  const upper = func.toUpperCase();
  if (upper.includes('GOVERN') || upper.startsWith('GV')) return 'GOVERN (GV)';
  if (upper.includes('IDENTIFY') || upper.startsWith('ID')) return 'IDENTIFY (ID)';
  if (upper.includes('PROTECT') || upper.startsWith('PR')) return 'PROTECT (PR)';
  if (upper.includes('DETECT') || upper.startsWith('DE')) return 'DETECT (DE)';
  if (upper.includes('RESPOND') || upper.startsWith('RS')) return 'RESPOND (RS)';
  if (upper.includes('RECOVER') || upper.startsWith('RC')) return 'RECOVER (RC)';
  return func;
};

/**
 * Map a maturity score (0-7 scale) to a label.
 */
const scoreToLabel = (score) => {
  if (score < 1) return 'Initial';
  if (score < 3) return 'Developing';
  if (score < 5) return 'Managed';
  return 'Optimized';
};

/**
 * Return an RGB color array based on gap size.
 * Green: gap <= 0, Yellow: gap < 2, Red: gap >= 2
 */
const gapColor = (gap) => {
  if (gap <= 0) return [34, 197, 94];    // green-500
  if (gap < 2)  return [234, 179, 8];   // yellow-500
  return [239, 68, 68];                 // red-500
};

/**
 * Generate a board-ready Executive Summary PDF for a CSF 2.0 assessment.
 *
 * @param {Object} params
 * @param {Object}   params.assessment   - The selected assessment object from assessmentsStore
 * @param {Array}    params.requirements - All requirements from requirementsStore
 * @param {Array}    params.findings     - All findings from findingsStore
 * @param {Array}    params.artifacts    - All artifacts from artifactStore
 * @param {number}   params.selectedQuarter - Quarter number 1-4 to summarise
 */
export const generateExecutiveSummary = ({
  assessment,
  requirements,
  findings,
  artifacts,
  selectedQuarter = 1,
}) => {
  if (!assessment) {
    console.error('generateExecutiveSummary: no assessment provided');
    return;
  }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth  = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginL = 15;
  const marginR = 15;
  const contentWidth = pageWidth - marginL - marginR;
  const quarterKey = `Q${selectedQuarter}`;

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const addPageNumber = () => {
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Generated by CSF Profile Assessment Tool  |  Page ${i} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
    }
  };

  // ─── Build Data ──────────────────────────────────────────────────────────────

  const { scopeIds, observations } = assessment;

  // Build a lookup: requirementId → function name
  const reqLookup = new Map();
  (requirements || []).forEach((req) => {
    if (req.id) {
      reqLookup.set(req.id, normalizeFunctionName(req.function));
    }
  });

  // Resolve function for each scopeId
  const resolveFunction = (itemId) => {
    if (reqLookup.has(itemId)) return reqLookup.get(itemId);
    const prefix = (itemId || '').substring(0, 2).toUpperCase();
    const map = {
      GV: 'GOVERN (GV)',
      ID: 'IDENTIFY (ID)',
      PR: 'PROTECT (PR)',
      DE: 'DETECT (DE)',
      RS: 'RESPOND (RS)',
      RC: 'RECOVER (RC)',
    };
    return map[prefix] || 'Unknown';
  };

  // Build per-item data for the selected quarter
  const items = (scopeIds || []).map((itemId) => {
    const obs = (observations || {})[itemId] || {};
    const qData = (obs.quarters || {})[quarterKey] || {};
    return {
      itemId,
      function: resolveFunction(itemId),
      actualScore: qData.actualScore ?? 0,
      targetScore: qData.targetScore ?? 0,
      gap: (qData.targetScore ?? 0) - (qData.actualScore ?? 0),
      linkedArtifacts: obs.linkedArtifacts || [],
    };
  });

  // Overall score (average actual across all items)
  const overallActual =
    items.length > 0
      ? +(items.reduce((s, i) => s + i.actualScore, 0) / items.length).toFixed(1)
      : 0;
  const overallTarget =
    items.length > 0
      ? +(items.reduce((s, i) => s + i.targetScore, 0) / items.length).toFixed(1)
      : 0;

  // Function-level aggregates
  const functionMap = {};
  FUNCTION_ORDER.forEach((fn) => {
    functionMap[fn] = { actualTotal: 0, targetTotal: 0, count: 0 };
  });
  items.forEach((item) => {
    const fn = item.function;
    if (!functionMap[fn]) {
      functionMap[fn] = { actualTotal: 0, targetTotal: 0, count: 0 };
    }
    functionMap[fn].actualTotal += item.actualScore;
    functionMap[fn].targetTotal += item.targetScore;
    functionMap[fn].count++;
  });

  const functionRows = FUNCTION_ORDER.map((fn) => {
    const g = functionMap[fn] || { actualTotal: 0, targetTotal: 0, count: 0 };
    const actual = g.count > 0 ? +(g.actualTotal / g.count).toFixed(1) : null;
    const target = g.count > 0 ? +(g.targetTotal / g.count).toFixed(1) : null;
    const gap    = actual !== null && target !== null ? +(target - actual).toFixed(1) : null;
    return { name: FUNCTION_SHORT[fn] || fn, actual, target, gap };
  }).filter((r) => r.actual !== null);

  // Top 5 priority gaps
  const top5Gaps = [...items]
    .filter((i) => i.gap > 0)
    .sort((a, b) => b.gap - a.gap)
    .slice(0, 5)
    .map((i) => ({
      id: i.itemId,
      gap: +i.gap.toFixed(1),
      function: FUNCTION_SHORT[i.function] || i.function,
    }));

  // Evidence coverage: items with at least one linked artifact / total items
  // Uses same logic as EvidenceTracker component for consistency
  const itemsWithEvidence = items.filter((i) => {
    return (i.linkedArtifacts || []).length > 0;
  });
  const evidenceCoverage =
    items.length > 0
      ? Math.round((itemsWithEvidence.length / items.length) * 100)
      : 0;

  // Findings summary
  const allFindings = findings || [];
  const totalFindings = allFindings.length;
  const byPriority = {
    Critical: allFindings.filter((f) => f.priority === 'Critical').length,
    High:     allFindings.filter((f) => f.priority === 'High').length,
    Medium:   allFindings.filter((f) => f.priority === 'Medium').length,
    Low:      allFindings.filter((f) => f.priority === 'Low').length,
  };
  const byStatus = {
    'Not Started': allFindings.filter((f) => f.status === 'Not Started').length,
    'In Progress': allFindings.filter((f) => f.status === 'In Progress').length,
    'Resolved':    allFindings.filter((f) => f.status === 'Resolved').length,
  };

  // ─── Page 1: Header + Scores ─────────────────────────────────────────────────

  let curY = 15;

  // Header bar
  doc.setFillColor(30, 64, 175); // blue-800
  doc.rect(0, 0, pageWidth, 28, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('CSF 2.0 Assessment — Executive Summary', pageWidth / 2, 12, { align: 'center' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const createdStr = assessment.createdDate
    ? new Date(assessment.createdDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'N/A';
  const generatedStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.text(`Assessment: ${assessment.name}`, marginL, 21);
  doc.text(`Created: ${createdStr}  |  Generated: ${generatedStr}  |  Quarter: Q${selectedQuarter}`, pageWidth - marginR, 21, { align: 'right' });

  curY = 36;

  // ── Overall Score ────────────────────────────────────────────────────────────

  doc.setFillColor(239, 246, 255); // blue-50
  doc.roundedRect(marginL, curY, contentWidth, 22, 3, 3, 'F');
  doc.setDrawColor(191, 219, 254); // blue-200
  doc.roundedRect(marginL, curY, contentWidth, 22, 3, 3, 'S');

  doc.setTextColor(30, 58, 138); // blue-900
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(
    `Overall Maturity Score: ${overallActual} / 7.0  —  ${scoreToLabel(overallActual)}`,
    pageWidth / 2,
    curY + 9,
    { align: 'center' }
  );

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(37, 99, 235); // blue-600
  doc.text(
    `Target: ${overallTarget} / 7.0  |  Gap to target: ${Math.max(0, overallTarget - overallActual).toFixed(1)}  |  ${items.length} items in scope`,
    pageWidth / 2,
    curY + 17,
    { align: 'center' }
  );

  curY += 30;

  // ── Function Scores Table ────────────────────────────────────────────────────

  doc.setTextColor(30, 41, 59); // slate-800
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`Function Scores — Q${selectedQuarter}`, marginL, curY);
  curY += 4;

  const functionTableRows = functionRows.map((r) => [
    r.name,
    r.actual !== null ? r.actual.toFixed(1) : '—',
    r.target !== null ? r.target.toFixed(1) : '—',
    r.gap !== null ? (r.gap > 0 ? r.gap.toFixed(1) : '0.0') : '—',
  ]);

  doc.autoTable({
    startY: curY,
    head: [['Function', 'Score', 'Target', 'Gap']],
    body: functionTableRows,
    margin: { left: marginL, right: marginR },
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: {
      fillColor: [30, 64, 175],
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center',
    },
    columnStyles: {
      0: { cellWidth: 70, halign: 'left' },
      1: { cellWidth: 30, halign: 'center' },
      2: { cellWidth: 30, halign: 'center' },
      3: { cellWidth: 30, halign: 'center' },
    },
    didDrawCell: (data) => {
      // Color-code the Gap column (index 3) in body rows
      if (data.section === 'body' && data.column.index === 3) {
        const gapVal = parseFloat(data.cell.raw);
        if (!isNaN(gapVal)) {
          const [r, g, b] = gapColor(gapVal);
          doc.setFillColor(r, g, b);
          doc.setTextColor(255, 255, 255);
          const { x, y, width, height } = data.cell;
          doc.rect(x, y, width, height, 'F');
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.text(
            data.cell.raw,
            x + width / 2,
            y + height / 2 + 1,
            { align: 'center' }
          );
          // Reset text color
          doc.setTextColor(30, 41, 59);
          doc.setFont('helvetica', 'normal');
        }
      }
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });

  curY = doc.lastAutoTable.finalY + 8;

  // ── Top 5 Priority Gaps ──────────────────────────────────────────────────────

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('Top 5 Priority Gaps', marginL, curY);
  curY += 4;

  if (top5Gaps.length === 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 116, 139);
    doc.text('No gaps identified for the selected quarter.', marginL, curY + 6);
    curY += 14;
  } else {
    const gapRows = top5Gaps.map((g, idx) => [
      `${idx + 1}`,
      g.id,
      g.gap.toFixed(1),
      g.function,
    ]);

    doc.autoTable({
      startY: curY,
      head: [['#', 'Requirement ID', 'Gap', 'Function']],
      body: gapRows,
      margin: { left: marginL, right: marginR },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: {
        fillColor: [127, 29, 29], // red-900
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 65, halign: 'left' },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 65, halign: 'left' },
      },
      alternateRowStyles: { fillColor: [255, 247, 237] }, // orange-50
    });

    curY = doc.lastAutoTable.finalY + 8;
  }

  // ── Evidence Coverage ────────────────────────────────────────────────────────

  // If we're running low on space, add a new page
  if (curY > pageHeight - 70) {
    doc.addPage();
    curY = 20;
  }

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('Evidence Coverage', marginL, curY);
  curY += 6;

  // Progress bar
  const barWidth = contentWidth;
  const barHeight = 8;
  const fillWidth = (evidenceCoverage / 100) * barWidth;

  doc.setFillColor(226, 232, 240); // slate-200 (background)
  doc.roundedRect(marginL, curY, barWidth, barHeight, 2, 2, 'F');

  const coverageColor = evidenceCoverage >= 70
    ? [34, 197, 94]  // green
    : evidenceCoverage >= 40
    ? [234, 179, 8]  // yellow
    : [239, 68, 68]; // red
  doc.setFillColor(...coverageColor);
  if (fillWidth > 0) {
    doc.roundedRect(marginL, curY, Math.max(fillWidth, 4), barHeight, 2, 2, 'F');
  }

  curY += barHeight + 4;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105); // slate-600
  doc.text(
    `Evidence Coverage: ${evidenceCoverage}%  (${itemsWithEvidence.length} of ${items.length} items have linked artifacts)`,
    marginL,
    curY
  );

  curY += 10;

  // ── Open Findings ────────────────────────────────────────────────────────────

  if (curY > pageHeight - 70) {
    doc.addPage();
    curY = 20;
  }

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('Open Findings Summary', marginL, curY);
  curY += 4;

  // Two-column layout: Priority | Status
  const halfW = (contentWidth - 8) / 2;

  // Priority breakdown table
  doc.autoTable({
    startY: curY,
    head: [['Priority', 'Count']],
    body: [
      ['Critical', byPriority.Critical],
      ['High',     byPriority.High],
      ['Medium',   byPriority.Medium],
      ['Low',      byPriority.Low],
      ['Total',    totalFindings],
    ],
    margin: { left: marginL, right: marginL + halfW + 8 },
    tableWidth: halfW,
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [71, 85, 105], textColor: 255, fontStyle: 'bold' },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'center', fontStyle: 'bold' },
    },
    didDrawCell: (data) => {
      if (data.section === 'body' && data.column.index === 0) {
        const colorMap = {
          Critical: [239, 68, 68],
          High:     [249, 115, 22],
          Medium:   [234, 179, 8],
          Low:      [34, 197, 94],
        };
        const color = colorMap[data.cell.raw];
        if (color) {
          // Draw colored dot before text
          const { x, y, height } = data.cell;
          doc.setFillColor(...color);
          doc.circle(x + 4, y + height / 2, 1.5, 'F');
        }
      }
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });

  const afterPriorityY = doc.lastAutoTable.finalY;

  // Status breakdown table (rendered side-by-side using x offset)
  doc.autoTable({
    startY: curY,
    head: [['Status', 'Count']],
    body: [
      ['Not Started', byStatus['Not Started']],
      ['In Progress', byStatus['In Progress']],
      ['Resolved',    byStatus['Resolved']],
    ],
    margin: { left: marginL + halfW + 8, right: marginR },
    tableWidth: halfW,
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [71, 85, 105], textColor: 255, fontStyle: 'bold' },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'center', fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });

  curY = Math.max(afterPriorityY, doc.lastAutoTable.finalY) + 8;

  // ── Maturity Scale Legend ─────────────────────────────────────────────────────

  if (curY > pageHeight - 40) {
    doc.addPage();
    curY = 20;
  }

  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 116, 139); // slate-500
  doc.text(
    'Maturity Scale: 0-1 Initial  |  1-3 Developing  |  3-5 Managed  |  5-7 Optimized',
    marginL,
    curY
  );
  doc.text(
    'Gap Color: Green (<=0, on/above target)  |  Yellow (<2, minor gap)  |  Red (>=2, significant gap)',
    marginL,
    curY + 5
  );

  // ── Page numbers (added last) ─────────────────────────────────────────────────

  addPageNumber();

  // ── Save ──────────────────────────────────────────────────────────────────────

  doc.save('CSF-Executive-Summary.pdf');
};
